import { supabase } from '@/lib/supabase';
import type { Lead } from '@/mocks/admin';

export interface Facilitador {
  id: string;
  nome: string;
  role: string;
  email: string;
  whatsapp: string;
  especialidade: string;
  cor_avatar: string;
  ativo: boolean;
  created_at: string;
}

export interface ProgramEnrollment {
  id: string;
  lead_id: string;
  facilitador_id: string;
  programa: string;
  status: string;
  data_inicio: string;
  data_fim_prevista: string;
  data_conclusao: string;
  progresso_total: number;
  observacoes: string;
  created_at: string;
  leads?: Lead;
  facilitadores?: Facilitador;
}

export interface ProgramModule {
  id: string;
  modulo_key: string;
  titulo: string;
  descricao: string;
  objetivo: string;
  entregavel: string;
  ferramenta: string;
  icon: string;
  ordem: number;
  ativo: boolean;
}

export interface ClientModuleProgress {
  id: string;
  enrollment_id: string;
  module_id: string;
  progresso: number;
  status: string;
  facilitador_id: string;
  data_inicio: string;
  data_entrega: string;
  notas: string;
  created_at: string;
  program_modules?: ProgramModule;
  facilitadores?: Facilitador;
}

export interface ClientDeliverable {
  id: string;
  client_module_id: string;
  titulo: string;
  descricao: string;
  status: string;
  data_prazo: string;
  data_entrega: string;
  arquivo_url: string;
  arquivo_nome: string;
  observacoes: string;
}

export interface FacilitatorSession {
  id: string;
  enrollment_id: string;
  client_module_id: string;
  facilitador_id: string;
  tipo: string;
  titulo: string;
  data_sessao: string;
  hora_sessao: string;
  duracao_min: number;
  local: string;
  link_meet: string;
  status: string;
  resumo: string;
  created_at: string;
  facilitadores?: Facilitador;
  client_module_progress?: ClientModuleProgress;
}

export interface ProgramPayment {
  id: string;
  enrollment_id: string;
  parcela: number;
  valor: number;
  data_vencimento: string;
  data_pagamento: string;
  status: string;
  observacoes: string;
}

// ── FACILITADORES ────────────────────────────────────────────
export const facilitadoresApi = {
  list: async (): Promise<Facilitador[]> => {
    const { data, error } = await supabase.from('facilitadores').select('*').eq('ativo', true).order('nome');
    if (error) throw error;
    return data || [];
  },
  create: async (f: Omit<Facilitador, 'id' | 'created_at'>): Promise<Facilitador> => {
    const { data, error } = await supabase.from('facilitadores').insert(f).select().single();
    if (error) throw error;
    return data;
  },
  update: async (id: string, f: Partial<Facilitador>): Promise<Facilitador> => {
    const { data, error } = await supabase.from('facilitadores').update(f).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },
  delete: async (id: string): Promise<void> => {
    const { error } = await supabase.from('facilitadores').update({ ativo: false }).eq('id', id);
    if (error) throw error;
  },
};

// ── ENROLLMENTS ─────────────────────────────────────────────
export const enrollmentsApi = {
  list: async (): Promise<ProgramEnrollment[]> => {
    const { data, error } = await supabase
      .from('program_enrollments')
      .select('*, leads(*), facilitadores(*)')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
  get: async (id: string): Promise<ProgramEnrollment | null> => {
    const { data, error } = await supabase
      .from('program_enrollments')
      .select('*, leads(*), facilitadores(*)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },
  create: async (e: Omit<ProgramEnrollment, 'id' | 'created_at' | 'leads' | 'facilitadores'>): Promise<ProgramEnrollment> => {
    const { data, error } = await supabase.from('program_enrollments').insert(e).select().single();
    if (error) throw error;
    return data;
  },
  update: async (id: string, e: Partial<ProgramEnrollment>): Promise<ProgramEnrollment> => {
    const { data, error } = await supabase.from('program_enrollments').update(e).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },
};

// ── MODULES ─────────────────────────────────────────────────
export const modulesApi = {
  list: async (): Promise<ProgramModule[]> => {
    const { data, error } = await supabase.from('program_modules').select('*').eq('ativo', true).order('ordem');
    if (error) throw error;
    return data || [];
  },
};

// ── CLIENT MODULE PROGRESS ──────────────────────────────────
export const progressApi = {
  list: async (enrollmentId: string): Promise<ClientModuleProgress[]> => {
    const { data, error } = await supabase
      .from('client_module_progress')
      .select('*, program_modules(*), facilitadores(*)')
      .eq('enrollment_id', enrollmentId)
      .order('program_modules(ordem)');
    if (error) throw error;
    return data || [];
  },
  upsert: async (p: Omit<ClientModuleProgress, 'id' | 'created_at' | 'program_modules' | 'facilitadores'>): Promise<ClientModuleProgress> => {
    const { data, error } = await supabase
      .from('client_module_progress')
      .upsert(p, { onConflict: 'enrollment_id,module_id' })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ── DELIVERABLES ────────────────────────────────────────────
export const deliverablesApi = {
  list: async (clientModuleId: string): Promise<ClientDeliverable[]> => {
    const { data, error } = await supabase.from('client_deliverables').select('*').eq('client_module_id', clientModuleId);
    if (error) throw error;
    return data || [];
  },
  upsert: async (d: Omit<ClientDeliverable, 'id'>): Promise<ClientDeliverable> => {
    const { data, error } = await supabase.from('client_deliverables').upsert(d).select().single();
    if (error) throw error;
    return data;
  },
};

// ── SESSIONS ────────────────────────────────────────────────
export const sessionsApi = {
  list: async (enrollmentId?: string): Promise<FacilitatorSession[]> => {
    let q = supabase.from('facilitator_sessions').select('*, facilitadores(*), client_module_progress(*)');
    if (enrollmentId) q = q.eq('enrollment_id', enrollmentId);
    q = q.order('data_sessao', { ascending: false });
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },
  create: async (s: Omit<FacilitatorSession, 'id' | 'created_at' | 'facilitadores' | 'client_module_progress'>): Promise<FacilitatorSession> => {
    const { data, error } = await supabase.from('facilitator_sessions').insert(s).select().single();
    if (error) throw error;
    return data;
  },
  update: async (id: string, s: Partial<FacilitatorSession>): Promise<FacilitatorSession> => {
    const { data, error } = await supabase.from('facilitator_sessions').update(s).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },
};

// ── PAYMENTS ────────────────────────────────────────────────
export const paymentsApi = {
  list: async (enrollmentId: string): Promise<ProgramPayment[]> => {
    const { data, error } = await supabase.from('program_payments').select('*').eq('enrollment_id', enrollmentId).order('parcela');
    if (error) throw error;
    return data || [];
  },
  upsert: async (p: Omit<ProgramPayment, 'id'>): Promise<ProgramPayment> => {
    const { data, error } = await supabase.from('program_payments').upsert(p).select().single();
    if (error) throw error;
    return data;
  },
};
